import { useState, useContext, useEffect } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { userLogin } from '../../services/auth';
import { AuthContext } from '../../context/auth';
import { CampaignContext } from '~/context/campaign';
import { RegistrationContext } from '../../context/registration';
import { useToasts } from 'react-toast-notifications';
import toast from 'react-hot-toast';

import { PasswordInput, TextInput } from '../../shared/components/form/Inputs';
import { CheckBox } from '../../shared/components/form/CheckBox';
import {
    Container,
    Button,
    Card,
    CardBody,
    Col,
    Row,
    Form,
    FormGroup,
    Alert,
} from 'reactstrap';
import { NoCampaignCard } from '../../shared/components/cards/TipsCards';
import { otherTypeCodes, contactPreferenceCopy } from '../../shared/config';

export const LogInForm = ({}: any) => {
    const [{ user }, userDispatch] = useContext(AuthContext);
    const [{ campaignRequest }, registrationDispatch] =
        useContext(RegistrationContext);
    const [{ showCampaignMessage }, campaignDispatch] =
        useContext(CampaignContext);
    const history = useHistory();
    const location = useLocation();

    const [showPassword, setShowPassword] = useState(false);
    const [checkingRegistration, setCheckingRegistration] = useState(false);
    const [showPasswordReset, setShowPasswordReset] = useState(false);
    const {
        register,
        handleSubmit,
        watch,
        control,
        reset,
        formState: { errors, isSubmitting },
    } = useForm({
        username: '',
        password: '',
    });
    const { addToast } = useToasts();
    if (errors) {
        if (
            !showPasswordReset &&
            errors.password &&
            errors.password.type === 'minLength'
        ) {
            addToast('Password must be at least 8 characters', {
                appearance: 'error',
            });
            setShowPasswordReset(true);
        }
    }

    const [rememberMe, setRememberMe] = useState(false);
    let token = localStorage.getItem('token');

    useEffect(() => {
        // Only clear authentication if user is not currently logged in
        // This prevents clearing tokens during redirects or if user is already authenticated
        if (!user && !token) {
            // Clear authentication token and user when visiting login page
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            
            // Clear user from context
            userDispatch({
                type: 'set_user',
                user: null,
            });
        }

        // Show toast if user was logged out due to inactivity
        console.log('[Login] Checking for sessionLogoutMessage:', localStorage.getItem('sessionLogoutMessage'));
        const logoutMessage = localStorage.getItem('sessionLogoutMessage');
        if (logoutMessage) {
            console.log('[Login] Showing toast:', logoutMessage);
            toast.error(logoutMessage);
            localStorage.removeItem('sessionLogoutMessage');
        } else {
            console.log('[Login] No sessionLogoutMessage found');
        }

        // Restore "remember me" credentials if they exist
        let storedUser = localStorage.getItem('userLogin');
        if (storedUser) {
            storedUser = JSON.parse(storedUser);
            console.log('storedUser', storedUser);
            reset({
                username: storedUser.username,
                password: storedUser.password,
            });
            setRememberMe(true);
        }
    }, []);

    const hasInsuranceOption = (application) => {
        console.log('application: ', application);
        if (!application) {
            return false;
        }
        if (
            application.ins_has_none ||
            application.ins_has_private ||
            application.ins_has_medicare ||
            application.ins_has_cobra ||
            application.ins_has_medicaid ||
            application.ins_unknown
        ) {
            return true;
        }
        return false;
    };

    const onSubmit = async (data) => {
        setCheckingRegistration(true);
        const resp = await userLogin(data.username, data.password);
        
        if (!resp.user) {
            addToast('Incorrect Credentials', { appearance: 'error' });
        } else {
            // Debug: Check if token was saved properly
            const savedToken = localStorage.getItem('token');
            if (rememberMe) {
                localStorage.setItem(
                    'userLogin',
                    JSON.stringify({
                        username: data.username,
                        password: data.password,
                    })
                );
            } else {
                localStorage.removeItem('userLogin');
            }

            userDispatch({
                type: 'set_user',
                user: resp.user,
            });

            campaignDispatch({
                type: 'set_show_campaign_message',
                payload: true,
            });

            // Get next parameter for redirect after login
            // Use window.location.search as fallback if location.search is empty
            const searchParams = location.search || window.location.search;
            const urlParams = new URLSearchParams(searchParams);
            const nextUrl = urlParams.get('next');

            // If superuser or staff admin (has role field from API), go to next URL or dashboard
            // Admin users get a 'role' field in the response (patient services coordinator, medical professional, Financial, or administrator)
            if (resp.user.is_superuser || resp.role) {
                setCheckingRegistration(false);
                history.push(nextUrl || '/dashboard');
                return;
            }

            // userDispatch({
            //   type: 'set_person',
            //   user: resp.person,
            // });

            // Only enable get-started routing if:
            // 1. Legacy password is disabled
            // 2. Get-started flow is explicitly enabled
            const useLegacyPassword = import.meta.env.VITE_USE_LEGACY_PASSWORD === 'true';
            const enableGetStartedFlow = import.meta.env.VITE_ENABLE_GET_STARTED_FLOW === 'true';
            
            if (resp.campaign_request && !useLegacyPassword && enableGetStartedFlow) {
                console.log('resp.campaign_request: ', resp.campaign_request);
                const cr = resp.campaign_request;
                const { campaign, patient, application } = resp;
                console.log('application: ', application);
                console.log('patient: ', patient);
                console.log('campaign: ', campaign);

                registrationDispatch({
                    type: 'set_campaign_request',
                    campaignRequest: cr,
                });

                // Determine application stage
                if (!cr.campaign_is_for) {
                    console.log('No patient designation, routing to /who');
                    setCheckingRegistration(false);
                    history.push('/get-started/who');
                } else {
                    const isSelf = cr.campaign_is_for?.id === 1;
                    const isOther = otherTypeCodes.includes(
                        cr.campaign_is_for?.code
                    );
                    const contact_preferences_step =
                        cr.contact_preferences_required_step;
                    if (
                        contact_preferences_step &&
                        !cr.contact_preferences_completed
                    ) {
                        setCheckingRegistration(false);
                        history.push('/get-started/contact-preference', {
                            copy: contactPreferenceCopy.default,
                        });
                    } else if (campaign) {
                        if (!patient) {
                            if (isOther && !cr.other_terms_completed) {
                                setCheckingRegistration(false);
                                history.push('/get-started/other/terms');
                            } else {
                                console.log(
                                    'campaign exists but no patient, route to who setup page'
                                );
                                setCheckingRegistration(false);
                                history.push('/get-started/patient/who');
                            }
                        } else if (campaign.campaign_type) {
                            if (campaign.campaign_status === 'Disbursable') {
                                userDispatch({
                                    type: 'set_user',
                                    user: resp.user,
                                });
                                setCheckingRegistration(false);
                                history.push(nextUrl || '/dashboard');
                                return;
                            }
                            const type = campaign.campaign_type;
                            if (patient.injury_time_frame) {
                                if (cr.medical_center) {
                                    if (campaign.goal) {
                                        if (application?.fin_num_in_household) {
                                            if (
                                                hasInsuranceOption(application)
                                            ) {
                                                if (cr.community_types) {
                                                    if (
                                                        !application?.ag_terms_completed
                                                    ) {
                                                        setCheckingRegistration(false);
                                                        history.push(
                                                            '/get-started/terms-of-service'
                                                        );
                                                    } else if (
                                                        !application?.ag_signature
                                                    ) {
                                                        setCheckingRegistration(false);
                                                        history.push(
                                                            '/get-started/agreements'
                                                        );
                                                    } else {
                                                        userDispatch({
                                                            type: 'set_user',
                                                            user: resp.user,
                                                        });
                                                        setCheckingRegistration(false);
                                                        history.push(
                                                            nextUrl || '/dashboard'
                                                        );
                                                    }
                                                } else {
                                                    setCheckingRegistration(false);
                                                    history.push(
                                                        '/get-started/community'
                                                    );
                                                }
                                            } else {
                                                setCheckingRegistration(false);
                                                history.push(
                                                    '/get-started/financials/insurance'
                                                );
                                            }
                                        } else {
                                            setCheckingRegistration(false);
                                            history.push(
                                                '/get-started/financials/contribution'
                                            );
                                        }
                                    } else {
                                        setCheckingRegistration(false);
                                        history.push(
                                            '/get-started/financials/cost'
                                        );
                                    }
                                } else {
                                    setCheckingRegistration(false);
                                    history.push(
                                        '/get-started/condition/medical-professional'
                                    );
                                }
                            } else {
                                setCheckingRegistration(false);
                                history.push('/get-started/condition/when');
                            }
                        } else {
                            setCheckingRegistration(false);
                            history.push('/get-started/condition/type');
                        }
                    } else {
                        setCheckingRegistration(false);
                        history.push('/get-started/condition/type');
                    }
                }
            } else {
                // If legacy password is enabled, get-started flow is disabled, or no campaign request, go to dashboard
                userDispatch({
                    type: 'set_user',
                    user: resp.user,
                });
                setCheckingRegistration(false);
                history.push(nextUrl || '/dashboard');
            }
        }
    };

    if (user && token && !checkingRegistration) {
        const searchParams = location.search || window.location.search;
        const urlParams = new URLSearchParams(searchParams);
        const nextUrl = urlParams.get('next');
        return <Redirect to={nextUrl || '/dashboard'} />;
    }

    const adminUrl =
        import.meta.env.VITE_ADMIN_URL || 'https://admin.helphopelive.org';

    return (
        <Container className='w-100' style={{ maxWidth: '960px' }}>
            <Row>
                <Col md='6' className='mb-4'>
                    <Card
                        className='h-100'
                        style={{ border: '1px solid #01AEB4' }}
                    >
                        <CardBody className='px-5 text-center'>
                            <h3 className='mb-3'>Login</h3>

                            <Form
                                className='text-start'
                                onSubmit={handleSubmit(onSubmit)}
                            >
                                <TextInput
                                    name='username'
                                    label='Your Email'
                                    type='email'
                                    control={control}
                                    errors={errors?.username?.message}
                                    required={true}
                                    ariaRequired={true}
                                    helperText='Must be in format a@b.com'
                                />
                                <PasswordInput
                                    name='password'
                                    label='Password'
                                    control={control}
                                    required={true}
                                    ariaRequired={true}
                                    helperText='Must contain at least 8 characters'
                                />

                                <FormGroup check className='mb-3 ps-0'>
                                    <CheckBox
                                        name='remember_me'
                                        label='Remember me'
                                        value={rememberMe}
                                        onChange={(e) =>
                                            setRememberMe(!rememberMe)
                                        }
                                    />
                                </FormGroup>

                                <div className='text-center'>
                                    {showPasswordReset && (
                                        <Alert
                                            color='warning'
                                            className='mt-4 p-3 small text-center'
                                        >
                                            We have launched new campaign
                                            features. To enjoy these updates,
                                            please{' '}
                                            <button 
                                                type="button"
                                                className="btn btn-link p-0"
                                                style={{ textTransform: 'none' }}
                                                onClick={() => history.push('/forgot-password')}
                                            >
                                                reset your password
                                            </button>{' '}
                                            to match our new login requirements.
                                        </Alert>
                                    )}
                                    <button 
                                        type="button"
                                        className="btn btn-link p-0"
                                        style={{ textTransform: 'none' }}
                                        onClick={() => history.push('/forgot-password')}
                                    >
                                        I forgot my password
                                    </button>
                                    <br />
                                    <br />

                                    <Button
                                        color='primary'
                                        disabled={isSubmitting}
                                        type='submit'
                                        aria-label="Submit login form"
                                    >
                                        Login
                                    </Button>
                                </div>

                                <Alert
                                    color='warning'
                                    className='mt-4 p-3 small text-center'
                                >
                                    Are you a medical professional?{' '}
                                    <a href={adminUrl} className='text-nowrap'>
                                        Log in here
                                    </a>
                                </Alert>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
                <Col md='6' className='mb-4'>
                    <NoCampaignCard />
                </Col>
            </Row>
        </Container>
    );
};
